import { useEffect, useRef, useState } from "react";
import { devWarn } from "@/lib/devLogger";

// `render=explicit` disables Cloudflare's implicit auto-scan of
// `.cf-turnstile` elements on script load. The container this hook binds
// to intentionally carries the `cf-turnstile` class + `data-action`
// marker (for DOM-level analytics/attribution tooling), but is rendered
// exclusively via the explicit `window.turnstile.render()` call below --
// without `render=explicit`, the script would ALSO auto-render that same
// div on load, causing a duplicate/broken widget.
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
// Telemetry marker (Cloudflare account-level aggregate only, never
// per-user) so activation of this integration is attributed correctly.
export const TURNSTILE_ACTION = "turnstile-spin-v2";

let scriptLoadPromise = null;

const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

/**
 * Owns the Turnstile widget's mount/render/cleanup lifecycle -- kept
 * separate from TurnstileWidget.js's presentational JSX so the lifecycle
 * logic (script loading, explicit render, teardown) is independently
 * readable/testable from the render output.
 *
 * Returns `{ error, reset }` -- `reset()` is exposed so the parent
 * component's imperative ref handle can trigger it after a submission.
 */
export const useTurnstileWidget = ({ containerRef, onVerify, onExpire }) => {
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: TURNSTILE_ACTION,
          theme: "dark",
          size: "flexible",
          callback: (token) => onVerify?.(token),
          "expired-callback": () => onExpire?.(),
          "error-callback": () => setError(true),
        });
      })
      .catch((err) => {
        devWarn("Turnstile widget could not be loaded:", err);
        setError(true);
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          // Expected if the widget was already removed/never mounted
          // (e.g. unmounted before the script finished loading) --
          // logged only for dev visibility, never surfaced to the user.
          devWarn("Turnstile widget cleanup: nothing to remove.", err);
        }
      }
    };
    // Intentionally mount-once: SITE_KEY/ACTION are module-level constants
    // (never change), and re-running this on every onVerify/onExpire
    // identity change (often a fresh inline arrow function from the
    // parent) would destroy and recreate the widget on every parent
    // re-render -- a worse bug than the lint rule it would satisfy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { error, reset };
};
